<template>
  <div class="add-quotation-modal-body">
	    <v-toolbar class="modal-toolbar" density="comfortable" flat>
	      <div class="modal-bar">
	        <v-btn icon variant="text" class="back-btn" aria-label="Back" @click="modal.close()">
	          <v-icon>mdi-arrow-left</v-icon>
	        </v-btn>
		        <div class="modal-title">{{ props.mode === "edit" ? "Edit section" : "Add section" }}</div>
	      </div>
	    </v-toolbar>

	    <v-card-text class="body py-6">
	      
	      <v-form class="form" @submit.prevent="handleSubmit">
	        <AppTextField v-model="name" label="Section name" placeholder="e.g. Services" />
	        <AppTextArea v-model="description" label="Description" placeholder="Enter section details..." rows="4" />

	        <div class="actions">
        
	          <AppButtonFlat
              block
              class="mb-4"
              type="submit"
              prepend-icon="mdi-check"
              :loading="isSubmitting"
              :disabled="isSubmitting || isDeleting || !isNameValid"
            >
	            {{ props.mode === "edit" ? "Save" : "Add" }}
	          </AppButtonFlat>

            <AppButtonFlat
              v-if="props.mode === 'edit'"
              block
              type="button"
              prepend-icon="mdi-delete-outline"
              color="error"
              variant="outlined"
              :loading="isDeleting"
              :disabled="isDeleting || isSubmitting"
              @click="handleDelete"
            >
              Delete
            </AppButtonFlat>
        </div>
      </v-form>
    </v-card-text>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
	import AppTextArea from "@/shared/components/ui/AppTextArea.vue";
	import { useModalStore } from "@/shared/stores/modal";
  import { useSnackbarStore } from "@/shared/stores/snackbar";
	import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
  import {
    deleteQuotationTemplateSection,
    saveQuotationTemplateSections,
  } from "@/modules/vendor/services/quotation-templates.api";

		export type QuotationSectionInput = {
		  title: string;
		  description: string;
		};

		const props = defineProps<{
      templateId: string | number;
      sectionId?: string | number;
		  mode?: "add" | "edit";
		  initial?: Partial<QuotationSectionInput>;
		  onAdd?: (quotationSection: QuotationSectionInput) => void;
		  onSave?: (quotationSection: QuotationSectionInput) => void;
      onDeleted?: () => void | Promise<void>;
		}>();

	const modal = useModalStore();
  const snackbar = useSnackbarStore();

		const name = ref(props.initial?.title ?? "");
		const description = ref(props.initial?.description ?? "");
    const isSubmitting = ref(false);
    const isDeleting = ref(false);

    const isNameValid = computed(() => Boolean(name.value.trim()));

	async function handleSubmit() {
	  if (!name.value.trim()) return;
    if (isSubmitting.value) return;

    isSubmitting.value = true;

		  const quotationSection: QuotationSectionInput = {
		    title: name.value.trim(),
		    description: description.value.trim(),
		  };

      try {
        const payload: Record<string, any> = {
          id: props.mode === "edit" ? props.sectionId ?? null : null,
          title: quotationSection.title,
          description: quotationSection.description || null,
        };

        await saveQuotationTemplateSections(props.templateId, payload);

		    if (props.mode === "edit") {
		      props.onSave?.(quotationSection);
		    } else {
		      props.onAdd?.(quotationSection);
		    }
		    modal.close();
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Unable to save section.";
        snackbar.error(message);
      } finally {
        isSubmitting.value = false;
      }
		}

    async function handleDelete() {
      if (isSubmitting.value || isDeleting.value) return;
      if (!props.sectionId) return;

      isDeleting.value = true;
      try {
        await deleteQuotationTemplateSection(props.sectionId);
        await props.onDeleted?.();
        modal.close();
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Unable to delete section.";
        snackbar.error(message);
      } finally {
        isDeleting.value = false;
      }
    }
</script>

<style scoped>
.add-quotation-modal-body {
  padding: 0 0 calc(env(safe-area-inset-bottom, 0px) + 16px);
  min-height: 100dvh;
}

.modal-toolbar {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--color-theme);
  padding: calc(env(safe-area-inset-top, 0px) + 8px) 0 0;
  height: auto;
}

.modal-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 0;
}

:deep(.modal-toolbar .v-toolbar__content) {
  height: auto;
  min-height: 64px;
  align-items: center;
  padding: 8px 16px;
}

.back-btn {
  width: 50px;
  height: 50px;
  border-radius: var(--radius-lg);
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}

.body {
  padding-top: 12px;
}

.form {
  display: grid;
  gap: 12px;
}

.actions {
  margin-top: 6px;
}
</style>
